import {FC, Suspense, lazy, useEffect, useState} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'

import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'

import {OnboardingRoutes} from '../modules/onboarding'
import {useAuth} from '../modules/auth'

import {InvestorCrmRoutes} from '../modules/investor-crm/InvestorCrmRoutes'
import {InvestorUpdatesRoutes} from '../modules/investor-updates/InvestorUpdatesRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import DataRoomsRoutes from '../modules/data-rooms/DataRoomsRoutes'
import FounderMarketRoutes from '../modules/founder-market/FounderMarketRoutes'
import ResourcesRoutes from '../modules/resources/ResourcesRoutes'
import DataRoomPreview from '../modules/data-rooms/view/DataRoomPreview'
import {CompanyPageRoutes} from '../modules/company-page/CompanyPageRoutes'
import SharedAccessRoutes from '../modules/shared-access/sharedAccessRoutes'
import StakeholderRoutes from '../modules/stakeholders/stakeholder-routes'
import {useModuleStatus} from '../hooks/useModuleStatus'
import DigitalSigning from '../modules/digital-signing/view/DigitalSigning'
import SettingsRoutes from '../modules/setting/settings-routes'
import ComplianceMeetingRoute from '../modules/compliance-meeting/ComplianceMeetingRoutes'
import AIBankerRoutes from '../modules/AI-banker/AIBankerRoutes'
import ComingSoon from '../modules/widgets/components/ComingSoon'
import {CaptableManagementRoutes} from '../modules/captable-management/CaptableManagementRoutes'
import {StartupShowcaseRoutes} from '../modules/startup-showcase/StartupShowcaseRoutes'
import {CoFounderConnectRoutes} from '../modules/cofounder-connect'
import {RecordsKeeperAIRoutes} from '../modules/recordskeeper-ai'
import {EarlyAccessInvite} from '../modules/dashboard/views/EarlyAccessInvite'
import {EarlyAccessRoutes} from '../modules/dashboard/EarlyAccessRoutes'
import {AuthLayout} from '../modules/auth/AuthLayout'
import PersonalBusinessOTP from '../modules/onboarding/views/PersonalBusinessOTP'
import {OTPRoutes} from '../modules/onboarding/OTPRoutes'
import QueryAndPrompt from '../modules/recordskeeper-ai/views/QueryAndPrompt'
import SuccessGuide from '../pages/SuccessGuide'
import ActivateSubscription from '../modules/dashboard/views/ActivateSubscription'
import AdminApp from '../modules/adminApp/views/AdminApp'

const InvestorDBRoutes = lazy(() => import('../modules//investor-database/InvestorDBRoutes'))
const isRkAppReady = process.env.REACT_APP_RK_APP_IS_READY === 'true'
const isRecordskeeper = process.env.REACT_APP_BRAND === 'RK'
export const app = process.env.REACT_APP_ENV
export const isComingSoon = process.env.REACT_APP_SHOW_COMING_SOON === 'true' ? true : false

const PrivateRoutes = () => {
  const {
    currentUser,
    storeCompanyId,
    companyId,
    newCompany,
    setRole,
    companyQueuePosition,
    setCompanyQueuePosition,
    setUseCase,
    planStatus,
    setPlanStatus,
    setInvoiceStatus,
  } = useAuth()
  const [ready, setReady] = useState(false)
  const {moduleStatus} = useModuleStatus()

  useEffect(() => {
    if (!companyId) {
      storeCompanyId(
        !!localStorage.getItem('companyId')
          ? parseInt(localStorage.getItem('companyId')!)
          : currentUser?.company[0]?.companyId
      )
      setUseCase(currentUser?.company[0]?.useCase)
      setCompanyQueuePosition(currentUser?.company[0]?.queuePosition)
      setPlanStatus(currentUser?.companyPlanStatus?.planStatus)
      setInvoiceStatus(currentUser?.companyPlanStatus?.invoiceStatus)
    }

    if (currentUser) {
      setReady(true)
    }

    if (+companyQueuePosition! > 0) {
      let storedData = localStorage.getItem('plan')
      if (storedData) {
        const existingPlanComponents = storedData.split('-')
        existingPlanComponents[0] = 'Basic'
        const newPlan = existingPlanComponents.join('-')
        localStorage.setItem('plan', newPlan)
      }
    }
  }, [currentUser, companyId, companyQueuePosition]) // eslint-disable-line

  useEffect(() => {
    if (currentUser && companyId) {
      const findCompany = currentUser.company.find(
        (comp: {companyId: number}) => comp.companyId === companyId
      )
      if (findCompany) {
        // if (isRecordskeeper) {
        setRole(findCompany?.roles?.roleName?.toLowerCase())
        // } else {
        //   setRole(findCompany?.roles?.user_role.roleId)
        // }
      }
    }
  }, [companyId, currentUser]) // eslint-disable-line
  
  return ready ? (
    <Routes>
      <Route path='/data-room/data-room-preview' element={<DataRoomPreview />} />
      {/* Redirect to Dashboard after success login/registartion */}
      {newCompany ?? companyId ? (
        <Route element={<MasterLayout />}>
          <Route path='success-guide' element={<SuccessGuide />} />
          <Route index element={<Navigate to='/dashboard' />} />
          <Route path='dashboard' element={<DashboardWrapper />} />

          {moduleStatus['investor-crm'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='investor-crm/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='investor-crm/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='investor-crm/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='investor-crm/*' element={<InvestorCrmRoutes />} />
          )}
          {moduleStatus['investor-updates'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='updates/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='updates/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='updates/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='updates/*'
              element={
                <SuspensedView>
                  <InvestorUpdatesRoutes />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='setting/*'
            element={
              <SuspensedView>
                <SettingsRoutes />
              </SuspensedView>
            }
          />

          {moduleStatus['data-rooms'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='data-rooms/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='data-rooms/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='data-rooms/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='data-rooms/*'
              element={
                <SuspensedView>
                  <DataRoomsRoutes />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['query-and-prompts'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='query-and-prompt'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='query-and-prompt'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='query-and-prompt'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='query-and-prompt'
              element={
                <SuspensedView>
                  <QueryAndPrompt />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['compliance-meeting'] !== true && app === 'PROD' && isComingSoon ? (
            <Route
              path='compliance-meeting/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='compliance-meeting/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='compliance-meeting/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='compliance-meeting/*'
              element={
                <SuspensedView>
                  <ComplianceMeetingRoute />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['digital signing'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='digital-signing/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='digital-signing/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='digital-signing/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='digital-signing/*'
              element={
                <SuspensedView>
                  <DigitalSigning />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['investor-database'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='investor-database/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='investor-database/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='investor-database/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='investor-database/*'
              element={
                <SuspensedView>
                  <InvestorDBRoutes />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['market-deals'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='founder-market/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='founder-market/*' element={<FounderMarketRoutes />} />
          )}

          {moduleStatus['shared-access'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='shared-access/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='shared-access/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='shared-access/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='shared-access/*' element={<SharedAccessRoutes />} />
          )}

          {moduleStatus['stakeholders'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='stakeholders/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='stakeholders/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='stakeholders/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='stakeholders/*' element={<StakeholderRoutes />} />
          )}

          {moduleStatus['resources'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='resources/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='resources/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='resources/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='resources/*'
              element={
                <SuspensedView>
                  <ResourcesRoutes />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['startup-showcase'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='startup-showcase/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='startup-showcase/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='startup-showcase/*'
              element={
                <SuspensedView>
                  <StartupShowcaseRoutes />
                </SuspensedView>
              }
            />
          )}
          {moduleStatus['cofounder-connect'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='cofounder-connect/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='cofounder-connect/*'
              element={
                <SuspensedView>
                  <CoFounderConnectRoutes />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['investment-banker-ai'] !== false && app === 'PROD' && isComingSoon ? (
            <Route
              path='investment-banker-ai/*'
              element={
                <SuspensedView>
                  <ComingSoon />
                </SuspensedView>
              }
            />
          ) : (
            <Route
              path='investment-banker-ai/*'
              element={
                <SuspensedView>
                  <AIBankerRoutes />
                </SuspensedView>
              }
            />
          )}

          {moduleStatus['company-page'] !== false && app === 'PROD' && isComingSoon ? (
            <Route path='company-page/*' element={<ComingSoon />} />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='company-page/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='company-page/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='company-page/*' element={<CompanyPageRoutes />} />
          )}

          {moduleStatus['captable-management'] !== false && app === 'PROD' && isComingSoon ? (
            <Route path='captable-management/*' element={<ComingSoon />} />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='captable-management/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='captable-management/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='captable-management/*' element={<CaptableManagementRoutes />} />
          )}
          <Route
            path='support/admin-app'
            element={
              <SuspensedView>
                <AdminApp />
              </SuspensedView>
            }
          />
          {moduleStatus['recordskeeper-ai'] !== false && app === 'PROD' && isComingSoon ? (
            <Route path='rk/*' element={<ComingSoon />} />
          ) : (!isRkAppReady && isRecordskeeper && companyQueuePosition !== 0) ||
            (companyQueuePosition !== 0 && isRecordskeeper) ? (
            <Route
              path='rk/*'
              element={
                <SuspensedView>
                  <EarlyAccessInvite />
                </SuspensedView>
              }
            />
          ) : planStatus === 'cancelled' && isRecordskeeper ? (
            <Route
              path='rk/*'
              element={
                <SuspensedView>
                  <ActivateSubscription />
                </SuspensedView>
              }
            />
          ) : (
            <Route path='rk/*' element={<RecordsKeeperAIRoutes />} />
          )}

          <Route
            path='activate-subscription/*'
            element={
              <SuspensedView>
                <ActivateSubscription />
              </SuspensedView>
            }
          />

          <Route
            path='early-access/*'
            element={
              <SuspensedView>
                <EarlyAccessRoutes />
              </SuspensedView>
            }
          />
          <Route path='*' element={<ErrorsPage />} />
        </Route>
      ) : (
        <>
          <Route index element={<Navigate to='/onboarding/' />} />
          <Route path='/onboarding/*' element={<OnboardingRoutes />} />
          <Route element={<AuthLayout />}>
            <Route path='/auth/info' element={<PersonalBusinessOTP />} />
            <Route path='/auth/verify-phone/*' element={<OTPRoutes />} />
          </Route>
          <Route element={<MasterLayout />}></Route>
          <Route path='*' element={<ErrorsPage />} />
        </>
      )}

      {/* Pages */}
    </Routes>
  ) : null
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
